/**
 * Escanear un código de barras con la cámara del teléfono.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  PARA QUÉ SIRVE ESTO, QUE NO ES LO QUE PARECE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * No viene a sustituir al lector láser: un láser es más rápido y no cansa el
 * brazo. Viene para **el negocio que todavía no tiene uno**, que son casi todos
 * al empezar, y para los ratos en que el mostrador se mueve — cargar mercancía
 * en el depósito, contar inventario entre los estantes, mirar el precio de algo
 * que un cliente trae en la mano.
 *
 * En esos ratos, la alternativa era teclear trece dígitos de memoria mirando
 * una etiqueta pequeña. Eso se hace mal una de cada tres veces.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ LA LIBRERÍA SE CARGA APARTE, Y QUÉ SIGNIFICA ESO DE VERDAD
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Leer códigos de barras por software pesa —casi medio mega— y la inmensa
 * mayoría de las veces que alguien abre Softfi no va a escanear nada con la
 * cámara. Metida en el paquete principal, el punto de venta tendría que leer y
 * arrancar ese medio mega en cada apertura, en un teléfono barato, para nada.
 *
 * Con el `import()` de aquí abajo queda en un archivo suyo, y el arranque de
 * Softfi ni lo mira hasta que alguien toca la cámara.
 *
 * ⚠️ Eso NO quiere decir que se descargue en ese momento. Softfi es una
 * aplicación instalable, y su instalador se baja **todos** los archivos por
 * detrás, este incluido. Es a propósito: sin eso, tocar la cámara sin internet
 * daría un error en vez de abrir el escáner, y este es justo uno de los sitios
 * donde no hay señal — el depósito, el fondo del local.
 *
 * Así que el precio se paga una vez, en segundo plano, y a cambio se escanea
 * también sin internet. Lo que no se paga nunca es el arranque.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ANDROID VA POR SU PROPIO CAMINO (2026-11-04)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Al principio había un solo camino a propósito: el lector de Android
 * (`BarcodeDetector`) no existe en el iPhone, y dos motores son más que arreglar.
 *
 * La prueba del dueño con un cliente cambió eso: 3 iPhone leían de una y a los 3
 * Android les costaba, con los mismos códigos. El iPhone sigue exactamente como
 * estaba, porque funciona, y Android va por `lib/camaraDeLectura.ts`.
 * El porqué está en `lib/lecturaDeCodigos.ts`.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { CameraOff, Check, Flashlight, X } from "lucide-react";

import { EscanerConTelefono } from "@/components/EscanerConTelefono";
import { Boton, cx } from "@/components/ui";
import { type Diagnostico, leerEnAndroid } from "@/lib/camaraDeLectura";
import {
  esAndroid,
  FORMATOS,
  hayCamara,
  limpiarCodigo,
  porQueNoAbrio,
  INTERVALO_DE_LECTURA,
  seEscaneaConLaCamara,
  trasVer,
  type UltimaLectura,
  valeLaLectura,
} from "@/lib/lecturaDeCodigos";

type Props = {
  abierto: boolean;
  onCerrar: () => void;
  /** Qué hacer con cada código leído. */
  onLeer: (codigo: string) => void;
  /**
   * Si la cámara sigue abierta después de leer.
   *
   * En el punto de venta sí: se pasan varios productos seguidos y cerrar y
   * volver a abrir es un segundo de cámara encendiéndose por cada uno. En los
   * buscadores no: se busca UNA cosa y lo siguiente es mirar el resultado.
   */
  continuo?: boolean;
  /** Qué se está buscando, para el texto de arriba. */
  titulo?: string;
};

/**
 * El escáner de cada casilla. En el teléfono abre la cámara; en la PC, el
 * teléfono unido a esa PC por un QR (2026-11-02, ver `EscanerConTelefono`).
 * Las pantallas no tienen que saber cuál: los dos llaman a `onLeer` igual.
 */
export function EscanerDeCodigo(props: Props) {
  return seEscaneaConLaCamara() ? <EscanerConCamara {...props} /> : <EscanerConTelefono {...props} />;
}

export function EscanerConCamara({
  abierto,
  onCerrar,
  onLeer,
  continuo = false,
  titulo = "Escanea el código",
  subtitulo,
  pie,
  alDiagnostico,
}: Props & {
  /** Debajo del título: la página del teléfono dice ahí a qué PC manda. */
  subtitulo?: ReactNode;
  /** En lugar de «lo último leído», abajo. */
  pie?: ReactNode;
  /** Qué cámara y qué lector se usan. Solo lo pide `/prueba-del-lector`. */
  alDiagnostico?: (d: Diagnostico) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const mirilla = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [encendiendo, setEncendiendo] = useState(true);
  const [leidos, setLeidos] = useState(0);
  const [ultimoTexto, setUltimoTexto] = useState<string | null>(null);
  const [hayLinterna, setHayLinterna] = useState(false);
  const [linterna, setLinterna] = useState(false);
  const [sugerencia, setSugerencia] = useState(false);

  /*
    Lo último que se aceptó, para no meter veinte veces el mismo producto.

    ⚠️ Va en una `ref` y no en un estado: la lectura ocurre en el bucle de la
    cámara, que se creó una sola vez. Un estado se le quedaría congelado en el
    valor del primer render y el freno no frenaría nada — el fallo clásico, y
    aquí acabaría en veinte harinas en el carrito.
  */
  const ultimo = useRef<UltimaLectura | null>(null);
  const pista = useRef<MediaStreamTrack | null>(null);
  /** Solo en Android: lo pedido a la cámara y cómo enfocar al tocar. */
  const ajustes = useRef<Record<string, unknown>[]>([]);
  const enfocar = useRef<((x: number, y: number) => void) | null>(null);

  /*
    Si pasa un rato sin leer, se dice qué probar. Casi siempre es que está
    demasiado cerca para enfocar: lo natural es acercarse, y eso lo empeora.
  */
  useEffect(() => {
    setSugerencia(false);
    if (!abierto || encendiendo || error) return;
    const t = window.setTimeout(() => setSugerencia(true), 5_000);
    return () => window.clearTimeout(t);
  }, [abierto, encendiendo, error, leidos]);

  useEffect(() => {
    if (!abierto) return;

    let vivo = true;
    let apagar: (() => void) | null = null;

    setError(null);
    setEncendiendo(true);
    setLeidos(0);
    setUltimoTexto(null);
    setLinterna(false);
    ultimo.current = null;

    async function encender() {
      if (!hayCamara()) {
        setError(
          "Este navegador no da acceso a la cámara. Escribe el código a mano o usa un lector.",
        );
        setEncendiendo(false);
        return;
      }

      /** Un código que vio la cámara, por cualquiera de los dos caminos. */
      const atender = (crudo: string) => {
        const codigo = limpiarCodigo(crudo);
        const ahora = Date.now();
        const vale = valeLaLectura(codigo, ultimo.current, ahora);
        // Se apunta SIEMPRE: que el mismo código siga delante es lo que
        // impide que vuelva a contar. Ver `lecturaDeCodigos.ts`.
        ultimo.current = trasVer(codigo, ultimo.current, ahora);
        if (!vale) return;

        avisarQueLeyo();
        setLeidos((n) => n + 1);
        setUltimoTexto(codigo);
        onLeer(codigo);

        if (!continuo) onCerrar();
      };

      try {
        if (esAndroid()) {
          const sesion = await leerEnAndroid({
            video: video.current!,
            mirilla: () => mirilla.current,
            alLeer: atender,
            sigueAbierto: () => vivo,
            alDiagnostico,
          });
          if (!sesion) return;

          apagar = sesion.detener;
          pista.current = sesion.pista;
          ajustes.current = sesion.ajustes;
          enfocar.current = sesion.enfocar;
          setHayLinterna(Boolean(sesion.capacidades.torch));
          setEncendiendo(false);
          return;
        }

        /*
          La librería, ahora sí. Ver el encabezado: se descarga al tocar el
          botón, no al abrir Softfi.
        */
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] =
          await Promise.all([import("@zxing/browser"), import("@zxing/library")]);
        if (!vivo) return;

        /*
          ⚠️ SOLO LOS FORMATOS QUE USA UN COMERCIO.

          Sin esta lista, la librería prueba TODOS los que sabe leer en cada
          cuadro — códigos postales japoneses incluidos. En el teléfono de 60
          dólares de una bodega, eso es la diferencia entre leer al instante y
          parecer que se colgó.

          La lista vive en `lecturaDeCodigos.ts` y aquí solo se traduce a lo que
          entiende la librería, para que quede en un sitio y no en dos.
        */
        const pistas = new Map([
          [
            DecodeHintType.POSSIBLE_FORMATS,
            FORMATOS.map((f) => BarcodeFormat[f]),
          ],
        ]);

        const lector = new BrowserMultiFormatReader(pistas, {
          // Ver `INTERVALO_DE_LECTURA`: con los 500 ms de fábrica, un cuadro
          // fallido parecía que el producto se había apartado.
          delayBetweenScanAttempts: INTERVALO_DE_LECTURA,
          delayBetweenScanSuccess: INTERVALO_DE_LECTURA,
        });
        const controles = await lector.decodeFromConstraints(
          {
            video: {
              /*
                ⚠️ LA DE ATRÁS, NO LA DE SELFIS.

                Sin esto, en muchos teléfonos abre la frontal y la persona se ve
                a sí misma intentando escanear. `ideal` y no `exact` a propósito:
                en una computadora portátil solo hay una cámara, y con `exact`
                el navegador contestaría que no hay ninguna en vez de usar la
                que tiene.
              */
              facingMode: { ideal: "environment" },
              // Suficiente para leer una etiqueta y poco para el teléfono.
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          },
          video.current!,
          (resultado) => {
            if (!vivo || !resultado) return;
            atender(resultado.getText());
          },
        );

        if (!vivo) {
          controles.stop();
          return;
        }

        apagar = () => controles.stop();
        setEncendiendo(false);
        alDiagnostico?.({
          motor: "zxing",
          camara: "Sin ajustes (este aparato no es Android)",
          resolucion: video.current ? `${video.current.videoWidth} × ${video.current.videoHeight}` : "?",
          enfoque: "Del aparato",
          zoom: "Sin zoom",
        });

        /*
          La linterna, si el aparato la deja encender.

          No es un adorno: media mercancía de una bodega está en un estante mal
          iluminado, y sin luz la cámara no engancha el código por mucho que se
          acerque. En iPhone casi nunca está disponible; por eso el botón solo
          sale cuando de verdad se puede.
        */
        const stream = video.current?.srcObject as MediaStream | null;
        const suPista = stream?.getVideoTracks()[0] ?? null;
        pista.current = suPista;
        const capacidades = suPista?.getCapabilities?.() as
          | { torch?: boolean }
          | undefined;
        setHayLinterna(Boolean(capacidades?.torch));
      } catch (e) {
        if (!vivo) return;
        setError(porQueNoAbrio(e));
        setEncendiendo(false);
      }
    }

    void encender();

    return () => {
      vivo = false;
      apagar?.();
      pista.current = null;
      ajustes.current = [];
      enfocar.current = null;
    };
    // `onLeer` y `onCerrar` se dejan fuera a propósito: cambian en cada render
    // del padre y volverían a encender la cámara sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, continuo]);

  async function alternarLinterna() {
    const suPista = pista.current;
    if (!suPista) return;
    const nuevo = !linterna;
    try {
      await suPista.applyConstraints({
        // Con lo demás detrás: según la norma, pedir solo la luz soltaría el zoom.
        advanced: [{ torch: nuevo }, ...ajustes.current] as MediaTrackConstraintSet[],
      });
      setLinterna(nuevo);
    } catch {
      // Hay aparatos que la anuncian y luego no la dan. Que falle la linterna
      // no puede cerrar el escáner: se sigue escaneando con la luz que haya.
      setHayLinterna(false);
    }
  }

  if (!abierto) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
    >
      {/* ── Arriba: qué se está haciendo y cómo salir ─────────────────────── */}
      <div className="flex items-center justify-between gap-3 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-white">{titulo}</p>
          {subtitulo}
        </div>

        {hayLinterna && (
          <button
            type="button"
            onClick={() => void alternarLinterna()}
            aria-label={linterna ? "Apagar la luz" : "Encender la luz"}
            aria-pressed={linterna}
            className={cx(
              "flex size-10 items-center justify-center rounded-full transition-colors",
              linterna ? "bg-white text-black" : "bg-white/15 text-white",
            )}
          >
            <Flashlight size={18} />
          </button>
        )}

        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="flex size-10 items-center justify-center rounded-full bg-white/15 text-white"
        >
          <X size={20} />
        </button>
      </div>

      {/* ── La cámara ────────────────────────────────────────────────────── */}
      <div
        className="relative flex-1 overflow-hidden"
        onClick={(e) => {
          // Tocar para enfocar, como en la cámara del teléfono: es lo que la
          // gente intenta cuando ve borroso.
          const caja = e.currentTarget.getBoundingClientRect();
          enfocar.current?.((e.clientX - caja.left) / caja.width, (e.clientY - caja.top) / caja.height);
        }}
      >
        <video
          ref={video}
          className="size-full object-cover"
          playsInline
          muted
          /*
            ⚠️ `playsInline` y `muted` no son adorno: sin los dos, el Safari del
            iPhone abre el vídeo a pantalla completa con sus propios controles
            encima y el escáner deja de verse.
          */
        />

        {/*
          La mirilla.

          Es un rectángulo ancho y bajo a propósito: un código de barras de
          producto tiene esa forma, y el marco le dice a la persona cómo poner el
          teléfono sin que nadie se lo explique. Con un cuadrado, la mitad de la
          gente lo pone vertical y no lee nunca.
        */}
        {!error && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-8">
            <div ref={mirilla} className="relative aspect-[5/3] w-full max-w-sm">
              <span className="absolute left-0 top-0 size-8 rounded-tl-lg border-l-4 border-t-4 border-white/90" />
              <span className="absolute right-0 top-0 size-8 rounded-tr-lg border-r-4 border-t-4 border-white/90" />
              <span className="absolute bottom-0 left-0 size-8 rounded-bl-lg border-b-4 border-l-4 border-white/90" />
              <span className="absolute bottom-0 right-0 size-8 rounded-br-lg border-b-4 border-r-4 border-white/90" />
            </div>
          </div>
        )}

        {encendiendo && !error && (
          <p className="absolute inset-x-0 bottom-6 text-center text-sm text-white/70">
            Encendiendo la cámara…
          </p>
        )}

        {sugerencia && !encendiendo && !error && (
          <p className="pointer-events-none absolute inset-x-6 bottom-6 text-center text-sm text-white">
            <span className="rounded-lg bg-black/60 box-decoration-clone px-2 py-1 leading-7">
              Si el código no se lee, aleja un poco el teléfono o inclínalo para evitar reflejos.
            </span>
          </p>
        )}

        {/* ── Cuando no se pudo ──────────────────────────────────────────── */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8 text-center">
            <CameraOff size={40} className="text-white/60" />
            <p className="max-w-sm text-sm leading-relaxed text-white/90">{error}</p>
            <Boton variante="secundario" onClick={onCerrar}>
              Escribir el código a mano
            </Boton>
          </div>
        )}
      </div>

      {/* ── Abajo: lo que va leyendo ─────────────────────────────────────── */}
      <div className="px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 text-center">
        {pie ?? (ultimoTexto ? (
          <p className="flex items-center justify-center gap-2 text-sm font-medium text-white">
            <Check size={16} className="text-emerald-400" />
            <span className="tabular truncate">{ultimoTexto}</span>
            {continuo && leidos > 1 && (
              <span className="tabular shrink-0 rounded-full bg-white/15 px-2 py-0.5 text-xs">
                {leidos}
              </span>
            )}
          </p>
        ) : (
          <p className="text-sm text-white/60">
            {continuo
              ? "Pasa los productos uno por uno. Se van agregando solos."
              : "Apunta al código de barras del producto."}
          </p>
        ))}
      </div>
    </div>
  );
}

/**
 * El pitido y el golpecito al leer.
 *
 * ⚠️ NO ES UN ADORNO: ES LA CONFIRMACIÓN SIN MIRAR.
 *
 * Quien escanea está mirando el producto, no la pantalla. Un lector láser pita
 * por eso mismo — el pitido es lo que dice «ya, pasa el siguiente». Sin él hay
 * que apartar la vista en cada producto para comprobar que entró, y ahí se va
 * toda la velocidad que la cámara podía dar.
 *
 * Se hace con el sonido del propio navegador y no con un archivo: un archivo
 * hay que descargarlo, guardarlo para que suene sin internet, y aun así el
 * navegador puede negarse a reproducirlo. Esto son cuatro líneas y suena igual.
 */
export function avisarQueLeyo(): void {
  try {
    navigator.vibrate?.(40);
  } catch {
    /* Los iPhone no vibran desde la web. No pasa nada: queda el pitido. */
  }

  try {
    const Audio = window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Audio) return;
    const ctx = new Audio();
    const oscilador = ctx.createOscillator();
    const volumen = ctx.createGain();

    oscilador.frequency.value = 1_760; // Un LA agudo: se oye por encima del ruido.
    volumen.gain.setValueAtTime(0.08, ctx.currentTime);
    volumen.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);

    oscilador.connect(volumen).connect(ctx.destination);
    oscilador.start();
    oscilador.stop(ctx.currentTime + 0.12);
    // Se cierra al terminar: dejar contextos abiertos gasta batería y hay
    // navegadores que solo permiten unos pocos a la vez.
    oscilador.onended = () => void ctx.close();
  } catch {
    /* Sin sonido se sigue escaneando igual. */
  }
}
